import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Logo, KitLogoLink } from "@/components/logo/types";

export function useLogoManagement(kitId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  // Fetch all available logos
  const { data: logos, isLoading: isLoadingLogos } = useQuery<Logo[]>({
    queryKey: ['logos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('logos')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching logos:', error);
        return [];
      }
      
      return data || [];
    }
  });

  // Fetch the active logo for this kit
  const { data: kitLogo, isLoading: isLoadingKitLogo } = useQuery<KitLogoLink | null>({
    queryKey: ['kit-logo', kitId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kit_logos')
        .select(`
          id,
          logo_id,
          is_active,
          logos:logo_id (
            id,
            storage_path
          )
        `)
        .eq('kit_id', kitId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching kit logo:', error);
        return null;
      }
      
      if (!data) return null;
      
      // Extract the logo data correctly
      const logoData = data.logos;
      
      // Properly structure the logo data to match the KitLogoLink type
      return {
        id: data.id,
        logo_id: data.logo_id,
        is_active: data.is_active,
        logo: logoData ? {
          id: logoData.id,
          storage_path: logoData.storage_path
        } : undefined
      };
    },
    enabled: !!kitId
  });

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file);
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data: logoData, error: logoError } = await supabase
        .from('logos')
        .insert({
          storage_path: filePath,
          filename: file.name,
          width: 400,
          height: 300,
          aspect_ratio: 400/300
        })
        .select()
        .single();
        
      if (logoError) {
        throw logoError;
      }
      
      queryClient.invalidateQueries({ queryKey: ['logos'] });
      
      toast({
        title: "Logo uploaded successfully",
        description: "Your logo has been uploaded and is ready to use."
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your logo. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  }, [queryClient, toast]);

  const setKitLogo = useCallback(async (logoId: string) => {
    try {
      await supabase
        .from('kit_logos')
        .update({ is_active: false })
        .eq('kit_id', kitId);
      
      const { data: existingLink } = await supabase
        .from('kit_logos')
        .select('id')
        .eq('kit_id', kitId)
        .eq('logo_id', logoId)
        .maybeSingle();
      
      if (existingLink) {
        await supabase
          .from('kit_logos')
          .update({ is_active: true })
          .eq('id', existingLink.id);
      } else {
        await supabase
          .from('kit_logos')
          .insert({
            kit_id: kitId,
            logo_id: logoId,
            is_active: true
          });
      }
      
      queryClient.invalidateQueries({ queryKey: ['kit-logo', kitId] });
      
      toast({
        title: "Logo updated",
        description: "Your ticker logo has been updated successfully."
      });
    } catch (error) {
      console.error('Error setting kit logo:', error);
      toast({
        title: "Update failed",
        description: "There was an error updating your logo. Please try again.",
        variant: "destructive"
      });
    }
  }, [kitId, queryClient, toast]);

  const removeKitLogo = useCallback(async () => {
    try {
      await supabase
        .from('kit_logos')
        .update({ is_active: false })
        .eq('kit_id', kitId);
      
      queryClient.invalidateQueries({ queryKey: ['kit-logo', kitId] });
      
      toast({
        title: "Logo removed",
        description: "The logo has been removed from this ticker."
      });
    } catch (error) {
      console.error('Error removing kit logo:', error);
      toast({
        title: "Update failed",
        description: "There was an error removing your logo. Please try again.",
        variant: "destructive"
      });
    }
  }, [kitId, queryClient, toast]);

  return {
    logos,
    isLoadingLogos,
    kitLogo,
    isLoadingKitLogo,
    isUploading,
    handleFileUpload,
    setKitLogo,
    removeKitLogo
  };
}
